import React, { useEffect } from "react";
import _ from "lodash";
import { useQuery, useSubscription } from "@apollo/react-hooks";
import {
  myLeaveMyFulfilledLeavesMobileTableDef as myFulfilledLeavesIsMobileTableDef,
  myLeaveMyFulfilledLeavesTableDef as myFulfilledLeavesTableDef,
} from "my-leave/configs/table-defs";
import {
  MY_LEAVES_BY_CONTRACT_SUBSCRIPTION,
  MY_LEAVE_GET_LEAVES_BY_CONTRACT,
} from "my-leave/graphql";
import { OcDataTable } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcTableState,
  useOcCurrentUserState,
  useOcPolling,
} from "oc/hooks";
import { getFilterValues } from "oc/utils/get-filter-values";
import getSortedList from "oc/utils/get-sorted-list";

type MyFulfilledLeavesProps = {
  selectedYear: number;
  isMobile: boolean;
};

export const MyFulfilledLeaves = ({
  selectedYear,
  isMobile,
}: MyFulfilledLeavesProps) => {
  let { contractCode } = useOcCurrentUserState();

  const { data, loading, refetch } = useQuery(MY_LEAVE_GET_LEAVES_BY_CONTRACT, {
    skip: !contractCode || !selectedYear,
    fetchPolicy: "cache-and-network",
    pollInterval: useOcPolling(1000 * 60),
    variables: { contract: contractCode, year: selectedYear },
  });

  useSubscription(MY_LEAVES_BY_CONTRACT_SUBSCRIPTION, {
    skip: !contractCode || !selectedYear,
    variables: { contract: contractCode, year: selectedYear },
    onData: () => {
      refetch();
    },
  });

  type Leave =
    & (typeof data extends { leavesByContract: (infer T)[] } ? T : any)
    & { _id?: string | number };

  let myFulfilledLeaves: Leave[] = [];

  if (data?.leavesByContract) {
    myFulfilledLeaves = _.cloneDeep(data.leavesByContract);
  }

  let tableId = "myFulfilledLeaves";

  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "startDate",
    pageSize: 5,
    filterField: "state",
    filterSelected: [] as string[],
    filterValues: [] as string[],
  });
  const [selectedRowId, setSelectedRowId] = React.useState<
    string | number | undefined
  >(undefined);

  myFulfilledLeaves.sort(
    getSortedList(
      tableData?.sortDirection === "desc"
        ? tableData?.sortField
        : `-${tableData?.sortField}`,
    ),
  );

  let list: {
    total: number;
    page: number;
    pageSize: number;
    rows: Leave[];
  } = {
    total: myFulfilledLeaves?.length || 0,
    page: tableData?.page,
    pageSize: tableData?.pageSize || initialTableData.pageSize,
    rows: myFulfilledLeaves || [],
  };

  const filterValues = tableData?.filterField
    ? getFilterValues(tableData.filterField, list.rows)
    : tableData?.filterValues;

  useEffect(() => {
    if (tableData?.filterField && !_.isEqual(filterValues, tableData?.filterValues)) {
      setTableData({
        ...tableData,
        filterValues,
      });
    }
  }, [filterValues, tableData, setTableData]);

  if (tableData?.filterSelected.length > 0) {
    list.rows = list.rows.filter((item) => {
      const key = item[tableData?.filterField as keyof Leave];
      return tableData?.filterSelected.includes(key);
    });
  }

  const tableDef = isMobile
    ? myFulfilledLeavesIsMobileTableDef
    : myFulfilledLeavesTableDef;

  const filteredTableDef = {
    id: myFulfilledLeavesTableDef?.id,
    columns: myFulfilledLeavesTableDef?.columns?.filter(
      (column) =>
        !myFulfilledLeavesIsMobileTableDef?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  const handleSelectedLeave = (o: Leave) => {
    setSelectedRowId((prev) => (prev === o?._id ? undefined : o?._id));
  };

  return (
    <OcDataTable
      data={list}
      loading={loading && list.rows.length === 0}
      tableDef={tableDef}
      tableData={tableData}
      setTableData={setTableData}
      pagination={true}
      localPaging={true}
      filteredTableDef={filteredTableDef}
      selectedRowId={selectedRowId}
      onSelectRow={handleSelectedLeave}
    />
  );
};

export default MyFulfilledLeaves;
